import videojs from 'video.js';

import { calcDimensions } from '../../util';
import { QueryStringParamProvider } from '../../implementations/player-page/helpers.js';
import './hotspots.css';

/**
 * The Hotspots plugin, which handles adding and rendering the hotspots
 * @extends {external:"videojs.Plugin"}
 **/
class HotspotsPlugin extends videojs.getPlugin('plugin') {
	constructor(player, options) {
		super(player, options);

		const paramProvider = new QueryStringParamProvider(location.search);
		this.shouldRenderHotspot = (paramProvider.getParamCaseSensitive("hotspots") !== "false");

		this.createHotspotHolder();

		this.player.one('loadedmetadata', () => {
			if (isNaN(this.aspect) || !this.aspect) {
				this.aspect = this.player.videoWidth() / this.player.videoHeight();
			}
		});
	}

	/**
	 * Creates the DOM element to hold the hotspots in that matches the size of the primary stream
	*/
	createHotspotHolder() {
		const Component = videojs.getComponent('Component');
		const hotspotsComponent = new Component(this.player, { name: 'Hotspots' });
		hotspotsComponent.addClass('mediasite-player__hotspots_wrapper');
		hotspotsComponent.addClass('vjs-hidden');
		this.hotspotsComponent = hotspotsComponent;

		const hotspotsContainer = document.createElement('div');
		hotspotsContainer.classList.add('mediasite-player__hotspots_container');
		this.hotspotsContainer = hotspotsContainer;

		hotspotsComponent.el().appendChild(hotspotsContainer);

		this.player.ready(() => {
			this.player.layout().addChild(hotspotsComponent);
		});
	}

	/**
	 * Creates the timed events for showing/hiding the hotspots on the presentation and then renders them
	 * @param {any} hotspots
	 */
	add(hotspots) {
		if (!this.shouldRenderHotspot) {
			return;
		}

		if (hotspots.length === 0) {
			return;
		}

		if (!this.renderedHotspots) {
			this.renderedHotspots = [];
			this.hotspotInfos = [];
		}

		for (let index = 0; index < hotspots.length; index++) {
			const hotspot = hotspots[index];
			const timedId = this.player.timedEvents().add(
				{
					time: hotspot.StartTime / 1000,
					duration: (hotspot.EndTime / 1000) - (hotspot.StartTime / 1000),
					type: 'hotspot'
				},
				this.showHotspot.bind(this),
				this.hideHotspot.bind(this)
			);

			hotspot.TimedId = timedId;

			//depneding if we have XR loaded, either render through XR or load it to the DOM
			if (videojs.getPlugin("video360")) {
				this.renderedHotspots[timedId] = this.player.video360?.().createCircleHotspot(hotspot);
			} else {
				this.renderedHotspots[timedId] = this.renderHotspot(hotspot);
			}

			this.hotspotInfos[timedId] = hotspot;
		}

		//since we've render hotspots, let's activate the raycaster to check if the user looks at them
		this.player.video360?.().createRaycaster(this.renderedHotspots);
		this.hotspotsComponent.removeClass('vjs-hidden');
	}

	// Displays the hotspot with the id given by the timed event
	showHotspot(id) {
		const targetHotspot = this.renderedHotspots[id];
		if (videojs.getPlugin("video360")) {
			targetHotspot.visible = true;
		} else {
			targetHotspot.classList.remove('vjs-hidden');
		}
	}

	// Hides the hotspot with the id given by the timed event
	hideHotspot(id) {
		const targetHotspot = this.renderedHotspots[id];
		if (videojs.getPlugin("video360")) {
			this.player.video360?.().deleteObject(targetHotspot);
		} else {
			targetHotspot.classList.add('vjs-hidden');
		}
	}

	// Triggers the event associated with the hotspot with said id
	triggerHotspot(id) {
		const hotspotInfo = this.hotspotInfos[id];
		const effect = hotspotInfo.InteractionEffect;
		const parameter = hotspotInfo.InteractionParam;

		//Deduce what the effect of the hotspot should be then call some sort of function with the parameter that was passed in
		switch (effect) {
			case "timeChange":
				if (parameter) {
					const targetTime = parameter / 1000;
					this.player.currentTime(targetTime);
				}
				break;
			case "presentationChange":
				if (parameter) {
					this.player.loader().loadNewPresentation(parameter);
				}
				break;
			default:
				this.player.log(`A hotspot with an unknown interaction effect of ${effect} has been trigger.`);
		}
	}

	/**
	 * Creates a DOM element for the hotspot. Update when non-circle sizing exists, along with any additional properities to a hotspot
	 * @param {any} hotspot
	 */
	renderHotspot(hotspot) {
		const hotspotDOM = document.createElement('div');
		hotspotDOM.classList.add('mediasite-player__hotspot', 'vjs-hidden');
		hotspotDOM.style.setProperty('height', `${hotspot.Height * 100}%`);
		hotspotDOM.style.setProperty('width', `${hotspot.Width * 100}%`);
		hotspotDOM.style.setProperty('left', `${hotspot.X * 100}%`);
		hotspotDOM.style.setProperty('top', `${hotspot.Y * 100}%`);

		hotspotDOM.addEventListener("click", () => {
			this.triggerHotspot(hotspot.TimedId);
		});

		this.hotspotsContainer.appendChild(hotspotDOM);
		return hotspotDOM;
	}

	deleteHotspot(id) {
		const targetHotspot = this.renderedHotspots[id];
		if (videojs.getPlugin("video360")) {
			targetHotspot.visible = false;
		} else {
			targetHotspot.remove();
		}
	}

	clear() {
		const timedEventIds = Object.keys(this.renderedHotspots);

		for (const id of timedEventIds) {
			this.deleteHotspot(id);
		}
	}
}

export default HotspotsPlugin;